import React, { useState } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Alert,
  Button,
  Paper,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Dashboard as DashboardIcon,
  People as CustomersIcon,
  Receipt as InvoicesIcon,
  Receipt as VATIcon,
  PlayArrow as PlayIcon,
  CheckCircle as CheckIcon,
  Flag as UKFlagIcon,
  Help as HelpIcon
} from '@mui/icons-material';

const UserGuidePage: React.FC = () => {
  const [expandedSection, setExpandedSection] = useState<string | false>('getting-started');

  const handleAccordionChange = (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedSection(isExpanded ? panel : false);
  };

  const guideSection = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: <PlayIcon color="primary" />,
      content: (
        <Box>
          <Typography variant="h6" gutterBottom>Welcome to EF-CapFlow</Typography>
          <Typography paragraph>
            EF-CapFlow is designed specifically for UK SMEs, providing modern financial management 
            with full HMRC compliance and superior user experience compared to traditional solutions like Sage or Xero.
          </Typography>
          
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>🚀 Quick Start Checklist</Typography>
            <List dense>
              <ListItem>
                <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                <ListItemText primary="Complete company setup" secondary="Add your business details and VAT information" />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                <ListItemText primary="Connect your bank account" secondary="Enable automatic transaction import via Open Banking" />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                <ListItemText primary="Add your first customers" secondary="Set up customer records with UK address validation" />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                <ListItemText primary="Create your first invoice" secondary="Professional invoices with automatic VAT calculations" />
              </ListItem>
            </List>
          </Alert>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Key Features Overview</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                  🇬🇧 UK-Specific Features
                </Typography>
                <List dense>
                  <ListItem><ListItemText primary="HMRC Making Tax Digital (MTD) compliance" /></ListItem>
                  <ListItem><ListItemText primary="Companies House integration" /></ListItem>
                  <ListItem><ListItemText primary="UK VAT rates and calculations" /></ListItem>
                  <ListItem><ListItemText primary="UK postcode and company number validation" /></ListItem>
                </List>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                  💡 Smart Features
                </Typography>
                <List dense>
                  <ListItem><ListItemText primary="AI-powered expense categorization" /></ListItem>
                  <ListItem><ListItemText primary="Cross-domain business intelligence" /></ListItem>
                  <ListItem><ListItemText primary="Automated compliance reminders" /></ListItem>
                  <ListItem><ListItemText primary="Real-time financial dashboards" /></ListItem>
                </List>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      )
    },
    {
      id: 'dashboard',
      title: 'Dashboard Overview',
      icon: <DashboardIcon color="primary" />,
      content: (
        <Box>
          <Typography variant="h6" gutterBottom>Understanding Your Financial Dashboard</Typography>
          <Typography paragraph>
            Your dashboard provides a real-time overview of your business finances with UK-specific metrics 
            and compliance status tracking.
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Key Metrics</Typography>
                  <List dense>
                    <ListItem>
                      <ListItemText 
                        primary="Total Revenue" 
                        secondary="Your total income for the current financial year"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Net Profit" 
                        secondary="Revenue minus expenses, with profit margin percentage"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Cash Balance" 
                        secondary="Current available funds across all connected accounts"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="VAT Liability" 
                        secondary="Amount owed to HMRC for current VAT period"
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>UK Compliance Status</Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon><VATIcon color="warning" /></ListItemIcon>
                      <ListItemText 
                        primary="VAT Returns" 
                        secondary="Next due date and submission status"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                      <ListItemText 
                        primary="Companies House" 
                        secondary="Annual return and confirmation statement dates"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><CheckIcon color="info" /></ListItemIcon>
                      <ListItemText 
                        primary="Corporation Tax" 
                        secondary="Estimated liability and payment dates"
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Alert severity="info" sx={{ mt: 3 }}>
            <Typography variant="subtitle2" gutterBottom>💡 Pro Tip</Typography>
            <Typography variant="body2">
              The dashboard updates in real-time as you add transactions, create invoices, and record expenses.
              Use the cash flow chart to identify seasonal trends and plan for upcoming expenses.
            </Typography>
          </Alert>
        </Box>
      )
    },
    {
      id: 'customers',
      title: 'Managing Customers',
      icon: <CustomersIcon color="primary" />,
      content: (
        <Box>
          <Typography variant="h6" gutterBottom>Customer Management</Typography>
          <Typography paragraph>
            Efficiently manage your customer relationships with UK-specific features including 
            address validation, credit scoring, and payment tracking.
          </Typography>

          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mt: 3 }}>
            Adding New Customers
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="1. Navigate to Customers" 
                secondary="Click 'Customers' in the sidebar navigation"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="2. Click 'Add Customer'" 
                secondary="Use the prominent 'Add Customer' button"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="3. Enter Customer Details" 
                secondary="Fill in name, contact information, and UK address"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="4. Set Payment Terms" 
                secondary="Configure credit limit and payment terms (e.g., 30 days)"
              />
            </ListItem>
          </List>

          <Paper sx={{ p: 2, mt: 3, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
            <Typography variant="subtitle2" gutterBottom>🇬🇧 UK Address Validation</Typography>
            <Typography variant="body2">
              Our system automatically validates UK postcodes and can lookup addresses using Royal Mail data. 
              This ensures accurate invoicing and delivery information.
            </Typography>
          </Paper>

          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mt: 3 }}>
            Customer Features
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <List dense>
                <ListItem>
                  <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                  <ListItemText primary="Credit limit management" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                  <ListItemText primary="Payment history tracking" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                  <ListItemText primary="Automated payment reminders" />
                </ListItem>
              </List>
            </Grid>
            <Grid item xs={12} md={6}>
              <List dense>
                <ListItem>
                  <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                  <ListItemText primary="Customer profitability analysis" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                  <ListItemText primary="Invoice history and aging" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                  <ListItemText primary="Customer communication log" />
                </ListItem>
              </List>
            </Grid>
          </Grid>
        </Box>
      )
    },
    {
      id: 'invoicing',
      title: 'Invoicing & Payments',
      icon: <InvoicesIcon color="primary" />,
      content: (
        <Box>
          <Typography variant="h6" gutterBottom>Professional Invoicing</Typography>
          <Typography paragraph>
            Create professional, VAT-compliant invoices that meet HMRC requirements and 
            help you get paid faster.
          </Typography>

          <Alert severity="success" sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>✅ HMRC Compliant Invoices</Typography>
            <Typography variant="body2">
              All invoices automatically include required HMRC information: VAT registration number, 
              correct VAT rates, and proper invoice numbering sequence.
            </Typography>
          </Alert>

          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
            Creating Your First Invoice
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="1. Select Customer" 
                secondary="Choose from existing customers or create new"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="2. Add Line Items" 
                secondary="Describe services/products with quantities and prices"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="3. VAT Calculation" 
                secondary="System automatically applies correct VAT rates"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="4. Review & Send" 
                secondary="Preview invoice and send via email or post"
              />
            </ListItem>
          </List>

          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mt: 3 }}>
            UK VAT Rates
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h4" color="primary">20%</Typography>
                <Typography variant="subtitle2">Standard Rate</Typography>
                <Typography variant="body2" color="text.secondary">
                  Most goods and services
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h4" color="success.main">5%</Typography>
                <Typography variant="subtitle2">Reduced Rate</Typography>
                <Typography variant="body2" color="text.secondary">
                  Energy, children's items
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h4" color="info.main">0%</Typography>
                <Typography variant="subtitle2">Zero Rate</Typography>
                <Typography variant="body2" color="text.secondary">
                  Books, food, children's clothes
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      )
    },
    {
      id: 'vat-compliance',
      title: 'VAT & Compliance',
      icon: <VATIcon color="primary" />,
      content: (
        <Box>
          <Typography variant="h6" gutterBottom>UK VAT & Compliance Management</Typography>
          <Typography paragraph>
            Stay compliant with HMRC requirements including Making Tax Digital (MTD) for VAT, 
            Companies House filings, and other UK business obligations.
          </Typography>

          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>📅 Important Deadlines</Typography>
            <Typography variant="body2">
              VAT returns are due one month and seven days after the end of your VAT period. 
              The system will automatically remind you of upcoming deadlines.
            </Typography>
          </Alert>

          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
            Making Tax Digital (MTD) Compliance
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
              <ListItemText 
                primary="Digital Record Keeping" 
                secondary="All transactions stored digitally with audit trail"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
              <ListItemText 
                primary="Digital VAT Returns" 
                secondary="Submit returns directly to HMRC via API"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
              <ListItemText 
                primary="Bridging Software" 
                secondary="Certified MTD-compatible software"
              />
            </ListItem>
          </List>

          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mt: 3 }}>
            VAT Return Process
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Quarterly Returns</Typography>
                <List dense>
                  <ListItem><ListItemText primary="Review transactions for VAT period" /></ListItem>
                  <ListItem><ListItemText primary="Check VAT calculations are correct" /></ListItem>
                  <ListItem><ListItemText primary="Submit return to HMRC" /></ListItem>
                  <ListItem><ListItemText primary="Make payment if VAT is due" /></ListItem>
                </List>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>System Features</Typography>
                <List dense>
                  <ListItem><ListItemText primary="Automatic VAT calculations" /></ListItem>
                  <ListItem><ListItemText primary="Pre-populated VAT return forms" /></ListItem>
                  <ListItem><ListItemText primary="Direct HMRC submission" /></ListItem>
                  <ListItem><ListItemText primary="Confirmation and receipts" /></ListItem>
                </List>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      )
    }
  ];

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <HelpIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
        <Typography variant="h3" gutterBottom sx={{ fontWeight: 600 }}>
          User Guide
        </Typography>
        <Typography variant="h6" color="text.secondary">
          Everything you need to know about EF-CapFlow
        </Typography>
        <Chip 
          icon={<UKFlagIcon />}
          label="UK-Specific Features" 
          color="secondary" 
          sx={{ mt: 2 }}
        />
      </Box>

      {/* Quick Navigation */}
      <Paper sx={{ p: 2, mb: 4 }}>
        <Typography variant="h6" gutterBottom>Quick Navigation</Typography>
        <Grid container spacing={1}>
          {guideSection.map((section) => (
            <Grid item key={section.id}>
              <Button
                variant={expandedSection === section.id ? "contained" : "outlined"}
                size="small"
                startIcon={section.icon}
                onClick={() => setExpandedSection(section.id)}
              >
                {section.title}
              </Button>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Guide Sections */}
      {guideSection.map((section) => (
        <Accordion
          key={section.id}
          expanded={expandedSection === section.id}
          onChange={handleAccordionChange(section.id)}
          sx={{ mb: 2 }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {section.icon}
              <Typography variant="h6">{section.title}</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {section.content}
          </AccordionDetails>
        </Accordion>
      ))}

      {/* Footer */}
      <Paper sx={{ p: 3, mt: 4, textAlign: 'center', bgcolor: 'primary.main', color: 'white' }}>
        <Typography variant="h6" gutterBottom>Need More Help?</Typography>
        <Typography paragraph>
          Can't find what you're looking for? Our UK-based support team is here to help.
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Button variant="outlined" sx={{ color: 'white', borderColor: 'white' }}>
            Live Chat
          </Button>
          <Button variant="outlined" sx={{ color: 'white', borderColor: 'white' }}>
            Email Support
          </Button>
          <Button variant="outlined" sx={{ color: 'white', borderColor: 'white' }}>
            Video Tutorials
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default UserGuidePage;
